/**
 * Global state management for real estate application
 * Handles userId and user data storage with session persistence
 * Uses caching to optimize performance and avoid multi-user conflicts
 */
import defaultLogo from '../assets/images/cb.png';
import defaultHeadshot from '../assets/images/shutterstock_299085002-large.jpg';

class GlobalState {
  constructor() {
    this.state = {
      userId: null,
      userData: null,
      listeners: []
    };
    this.storageKey = 'realEstateApp_globalState';

    // Cache configuration for performance optimization
    this.cache = {
      urlParams: null,
      urlParamsTimestamp: 0,
      userDataByUserId: new Map(),
      constants: null,
      constantsTimestamp: 0
    };

    // Cache timeout: 5 minutes
    this.cacheTimeout = 5 * 60 * 1000;
  }

  /**
   * Set the current userId and persist to session
   * @param {string} userId - User ID
   */
  setUserId(userId) {
    this.state.userId = userId;
    this.saveToSession();
  }

  /**
   * Get the current userId, loading from session if needed
   * @returns {string|null} Current userId or null
   */
  getUserId() {
    if (!this.state.userId) {
      this.loadFromSession();
    }
    return this.state.userId;
  }

  /**
   * Set user data and persist to session
   * @param {Object} userData - User data object
   */
  setUserData(userData) {
    this.state.userData = userData;
    this.saveToSession();
    this.notifyListeners();
  }


  /**
   * Get current user data, loading from session if needed
   * @returns {Object|null} Current user data or null
   */
  getUserData() {
    if (!this.state.userData) {
      this.loadFromSession();
    }
    return this.state.userData;
  }


  /**
   * Generic method to get user parameters with fallback chain
   * Priority: current userData > userId lookup > URL params > constants > default
   * @param {string} paramName - Parameter name in userData
   * @param {string} urlParamName - Parameter name in URL/constants
   * @param {string} defaultValue - Default value if nothing found
   * @returns {string} Parameter value
   */
  getUserParam(paramName, urlParamName, defaultValue) {
    // Try current user data first
    if (this.state.userData && this.state.userData[paramName]) {
      return this.state.userData[paramName];
    }

    // Try userId-based lookup
    if (this.state.userId) {
      try {
        const localData = localStorage.getItem('users_SEO_data');
        if (localData) {
          const users = JSON.parse(localData);
          const user = users.find(u => u.userId === this.state.userId);
          if (user && user[paramName]) {
            return user[paramName];
          }
        }
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      }
    }

    // Try URL parameters
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const value = urlParams.get(paramName) || urlParams.get(urlParamName);
      if (value) {
        return value;
      }
    }

    // Fall back to constants or default
    try {
      const constants = require('../constants/constants');
      return constants?.default?.[urlParamName] || defaultValue;
    } catch (error) {
      return defaultValue;
    }
  }

  /**
   * Get current agent name with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} Agent name
   */
  getCurrentAgentName() {
    return this.getUserParam('agentName', 'AGENT_NAME', 'Default Agent Name');
  }

  /**
   * Get current agent title with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} Agent title
   */
  getCurrentAgentTitle() {
    return this.getUserParam('agentTitle', 'AGENT_TITLE', 'Default Agent Title');
  }

  /**
   * Get current agent number with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} Agent number
   */
  getCurrentAgentNumber() {
    return this.getUserParam('agentNumber', 'AGENT_NUMBER', 'Default Agent Number');
  }

  /**
   * Get current agent email with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} Agent email
   */
  getCurrentAgentEmail() {
    return this.getUserParam('agentEmail', 'AGENT_EMAIL', 'Default Agent Email');
  }

  /**
   * Get current brokerage with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} Brokerage name
   */
  getCurrentBrokerage() {
    return this.getUserParam('brokerage', 'BROKERAGE', 'Default Brokerage');
  }

  /**
   * Get current quote with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} Quote text
   */
  getCurrentQuote() {
    return this.getUserParam('quote', 'QUOTE', 'Helping you feel at home is my passion');
  }

  /**
   * Get current about with fallback priority: userData > userId lookup > URL params > constants > default
   * @returns {string} About text
   */
  getCurrentAbout() {
    return this.getUserParam('about', 'ABOUT', 'With 20 years in the real estate business, I know what it takes to succeed. You can always trust my experience and expertise to help you make the right choice.');
  }

  /**
   * Get current logo with fallback priority: userData > userId lookup > URL params > constants > default
   * Handles binary data from database
   * @returns {string} Logo data URL or null
   */
  getCurrentLogo() {
    // Check if we have binary logo data in separate fields (MongoDB format)
    const logoImageData = this.getUserParam('logoImageData', 'LOGO_IMAGE_DATA', null);
    const logoContentType = this.getUserParam('logoContentType', 'LOGO_CONTENT_TYPE', null);
    
    // If we have binary data, convert it to data URL
    if (logoImageData && logoContentType) {
      return this.convertBinaryToDataUrl(logoImageData, logoContentType);
    }
    
    const logoUrl = this.getUserParam('logo', 'LOGO', defaultLogo);
    return logoUrl || defaultLogo;
  }

  /**
   * Get current headshot with fallback priority: userData > userId lookup > URL params > constants > default
   * Handles binary data from database
   * @returns {string} Headshot data URL or null
   */
  getCurrentHeadshot() {
    // Check if we have binary headshot data in separate fields (MongoDB format)
    const headshotImageData = this.getUserParam('headshotImageData', 'HEADSHOT_IMAGE_DATA', null);
    const headshotContentType = this.getUserParam('headshotContentType', 'HEADSHOT_CONTENT_TYPE', null);
    
    // If we have binary data, convert it to data URL
    if (headshotImageData && headshotContentType) {
      return this.convertBinaryToDataUrl(headshotImageData, headshotContentType);
    }
    
    const headshotUrl = this.getUserParam('headshot', 'HEADSHOT', defaultHeadshot);
    return headshotUrl || defaultHeadshot;
  }

  /**
   * Get current testimonials with fallback priority: userData > userId lookup > constants > default
   * @returns {Array} Testimonials array
   */
  getCurrentTestimonials() {
    const fromUserData = this.state.userData?.testimonials || this.state.userData?.TESTIMONIALS;
    if (fromUserData) {
      return fromUserData;
    }

    if (this.state.userId) {
      try {
        const localData = localStorage.getItem('users_SEO_data');
        if (localData) {
          const users = JSON.parse(localData);
          const user = users.find(u => u.userId === this.state.userId);
          const fromStorage = user?.testimonials || user?.TESTIMONIALS;
          if (fromStorage) {
            return fromStorage;
          }
        }
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      }
    }

    try {
      const constants = require('../constants/constants');
      return constants?.default?.TESTIMONIALS || constants?.TESTIMONIALS || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Convert binary logo data to data URL for display
   * @param {string|Object} binaryData - Base64 encoded binary data or MongoDB Binary object
   * @param {string} contentType - MIME type (e.g., "image/png", "image/jpeg")
   * @returns {string} Data URL for display
   */
  convertBinaryToDataUrl(binaryData, contentType) {
    try {
      let base64String = '';
      
      // Handle different binary data formats
      if (typeof binaryData === 'string') {
        // If it's already a base64 string, use it directly
        base64String = binaryData;
      } else if (typeof binaryData === 'object') {
        // Handle MongoDB Binary object format
        if (binaryData.base64) {
          base64String = binaryData.base64;
        } else if (binaryData.buffer) {
          // Convert buffer to base64
          base64String = Buffer.from(binaryData.buffer).toString('base64');
        } else if (binaryData.data) {
          // Handle other binary object formats
          base64String = binaryData.data;
        } else {
          console.warn('Unknown binary data format:', binaryData);
          return null;
        }
      } else {
        console.warn('Invalid binary data type:', typeof binaryData);
        return null;
      }
      
      // Clean up base64 string (remove any whitespace or newlines)
      base64String = base64String.replace(/\s/g, '');
      
      // Create data URL
      return `data:${contentType};base64,${base64String}`;
    } catch (error) {
      console.error('Error converting binary logo data to data URL:', error);
      return null;
    }
  }


  /**
   * Get all user parameters in a single object
   * @returns {Object} Object containing all user parameters
   */
  getAllUserParams() {
    return {
      agentName: this.getCurrentAgentName(),
      agentTitle: this.getCurrentAgentTitle(),
      agentNumber: this.getCurrentAgentNumber(),
      agentEmail: this.getCurrentAgentEmail(),
      brokerage: this.getCurrentBrokerage(),
      quote: this.getCurrentQuote(),
      about: this.getCurrentAbout(),
      logo: this.getCurrentLogo(),
      headshot: this.getCurrentHeadshot(),
      testimonials: this.getCurrentTestimonials()
    };
  }

  /**
   * Add a listener for state changes
   * @param {Function} listener - Callback function to be called on state changes
   * @returns {Function} Unsubscribe function
   */
  addListener(listener) {
    this.state.listeners.push(listener);
    return () => {
      const index = this.state.listeners.indexOf(listener);
      if (index > -1) {
        this.state.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of state changes
   */
  notifyListeners() {
    this.state.listeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('Error in state listener:', error);
      }
    });
  }


  /**
   * Save current state to sessionStorage
   */
  saveToSession() {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const stateToSave = {
          userId: this.state.userId,
          userData: this.state.userData,
          timestamp: Date.now()
        };
        sessionStorage.setItem(this.storageKey, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving to sessionStorage:', error);
      }
    }
  }

  /**
   * Load state from sessionStorage with expiration check (24 hours)
   */
  loadFromSession() {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const savedState = sessionStorage.getItem(this.storageKey);
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          const isExpired = Date.now() - parsedState.timestamp > 24 * 60 * 60 * 1000;
          if (!isExpired) {
            this.state.userId = parsedState.userId;
            this.state.userData = parsedState.userData;
          } else {
            this.clearSession();
          }
        }
      } catch (error) {
        console.error('Error loading from sessionStorage:', error);
        this.clearSession();
      }
    }
  }

  /**
   * Clear sessionStorage and reset state
   */
  clearSession() {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.removeItem(this.storageKey);
        this.state.userId = null;
        this.state.userData = null;
      } catch (error) {
        console.error('Error clearing sessionStorage:', error);
      }
    }
  }

  /**
   * Initialize global state from session and URL parameters
   * Loads existing state from sessionStorage and updates with URL params if different
   */
  initialize() {
    if (typeof window !== 'undefined') {
      this.loadFromSession();

      const urlParams = new URLSearchParams(window.location.search);
      const urlUserId = urlParams.get('userId') || urlParams.get('userId');

      if (urlUserId && this.state.userId !== urlUserId) {
        this.setUserId(urlUserId);
        // Try to find user data in localStorage
        let userData = null;
        try {
          const localData = localStorage.getItem('users_SEO_data');
          if (localData) {
            const users = JSON.parse(localData);
            userData = users.find(u => u.userId === urlUserId);
          }
        } catch (error) {
          console.error('Error reading from localStorage:', error);
        }

        if (userData) {
          this.setUserData(userData);
        } else {
          // Create user data from URL parameters if not found in storage
          const urlUserData = {
            userId: urlUserId,
            agentName: urlParams.get('agentName') || urlParams.get('AGENT_NAME'),
            agentTitle: urlParams.get('agentTitle') || urlParams.get('AGENT_TITLE'),
            agentNumber: urlParams.get('agentNumber') || urlParams.get('AGENT_NUMBER'),
            agentEmail: urlParams.get('agentEmail') || urlParams.get('AGENT_EMAIL'),
            brokerage: urlParams.get('brokerage') || urlParams.get('BROKERAGE'),
            logo: urlParams.get('logo') || urlParams.get('LOGO')
          };
          this.setUserData(urlUserData);
        }
      }
    }
  }


  /**
   * Get debug information about current state
   * @returns {Object} Debug information object
   */
  getDebugInfo() {
    return {
      userId: this.state.userId,
      userData: this.state.userData,
      agentName: this.getCurrentAgentName(),
      agentTitle: this.getCurrentAgentTitle(),
      agentNumber: this.getCurrentAgentNumber(),
      agentEmail: this.getCurrentAgentEmail(),
      brokerage: this.getCurrentBrokerage(),
      quote: this.getCurrentQuote(),
      about: this.getCurrentAbout(),
      logo: this.getCurrentLogo(),
      headshot: this.getCurrentHeadshot(),
      testimonials: this.getCurrentTestimonials()
    };
  }
}

// Create global state instance
const globalState = new GlobalState();

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  globalState.initialize();
}

export default globalState;

